<template>
  <v-dialog :value="value" max-width="640" scrollable @input="onInput">
    <section ref="panel" class="job-activity">
      <header class="job-activity__head">
        <div>
          <h2 id="job-activity-title">Job activity</h2>
          <p class="job-activity__identity">{{ job ? job.title : '' }} · {{ shortId }}</p>
        </div>
        <v-btn icon aria-label="Close job activity" @click="onInput(false)"><v-icon>mdi-close</v-icon></v-btn>
      </header>

      <div class="job-activity__body">
        <div v-if="loadError" class="job-activity__notice" role="alert">
          <strong>{{ activity ? 'Could not refresh activity' : 'Could not load activity' }}</strong>
          <p>{{ activity ? 'Showing the last activity received. Try again to get the latest update.' : 'Try again to load this job’s activity.' }}</p>
          <v-btn outlined small :loading="loading" :disabled="loading" @click="retry">Try again</v-btn>
        </div>
        <div v-if="loading && !activity" class="job-activity__loading" role="status">
          <v-progress-circular indeterminate :size="24" :width="2" color="primary" />
          <span>Loading activity…</span>
        </div>
        <template v-if="activity">
          <section class="job-activity__summary" aria-label="Current job status">
            <div class="job-activity__summary-top" role="status" aria-atomic="true">
              <div>
                <p class="job-activity__stage">{{ activity.stage || 'Stage not recorded' }}</p>
                <p class="job-activity__muted">{{ progress === null ? 'Progress has not been reported yet.' : `${progress}% complete` }}</p>
              </div>
              <span class="job-activity__badge" :class="statusClass">{{ statusLabel }}</span>
            </div>
            <v-progress-linear v-if="progress !== null" :value="progress" height="6" color="primary" class="job-activity__progress" aria-label="Analysis progress" />
            <p class="job-activity__updated job-activity__muted">{{ activity.updatedAt ? `Latest update · ${formatTime(activity.updatedAt)}` : 'Latest update not recorded' }}</p>
          </section>
          <div v-if="!activity.isAvailable" class="job-activity__notice">
            <strong>No activity recorded for this run</strong>
            <p>Activity tracking is available for new runs.</p>
          </div>
          <section v-else aria-labelledby="job-activity-timeline">
            <h3 id="job-activity-timeline">Activity timeline</h3>
            <p v-if="!activity.events.length" class="job-activity__muted">No activity has been recorded yet.</p>
            <ol v-else class="job-activity__timeline">
              <li v-for="event in activity.events" :key="event.id" :class="`job-activity__event--${eventLevel(event.level)}`">
                <v-icon :size="17" class="job-activity__event-icon" aria-hidden="true">{{ eventIcon(event.level) }}</v-icon>
                <p class="job-activity__event-meta"><time :datetime="event.occurredAt">{{ formatTime(event.occurredAt) }}</time> · {{ eventLevel(event.level) }}</p>
                <p>{{ event.message }}</p>
              </li>
            </ol>
          </section>
        </template>
      </div>

      <footer class="job-activity__foot">
        <span class="job-activity__muted">{{ refreshNote }}</span>
        <v-btn text @click="onInput(false)">Close</v-btn>
      </footer>
    </section>
  </v-dialog>
</template>

<script>
  import { $formatDate } from '@/utils';
  import analysisJobService from '../../service/analysisJob.service';
  import { shortJobId } from '../../utils/jobsFormat';
  import { activityProgress, activityStatusLabel, createJobActivityPoller, isActivityTerminal } from '../../utils/jobActivity';

  const EVENT_ICONS = { info: 'mdi-information-outline', warning: 'mdi-alert-outline', error: 'mdi-alert-circle-outline', success: 'mdi-check-circle-outline' };

  export default {
    name: 'JobActivityDialog',
    props: {
      value: { type: Boolean, default: false },
      job: { type: Object, default: null },
    },
    data() {
      return { activity: null, loading: false, loadError: false, settling: false };
    },
    computed: {
      activeJobId() { return this.value && this.job ? this.job.id : null; },
      shortId() { return shortJobId(this.job && this.job.id); },
      progress() { return activityProgress(this.activity && this.activity.progress); },
      statusLabel() { return activityStatusLabel(this.activity && this.activity.status); },
      statusClass() { return `job-activity__badge--${this.activity.status.toLowerCase()}`; },
      refreshNote() {
        if (this.settling) return 'Checking for the final activity update.';
        const status = this.activity ? this.activity.status : this.job && this.job.status;
        if (isActivityTerminal(status)) return 'This run is no longer updating.';
        return this.loadError ? 'Retrying while this panel is open.' : 'Updates every 5 seconds while this panel is open.';
      },
    },
    watch: {
      activeJobId() { this.startSession(); },
    },
    created() {
      this.activityPoller = createJobActivityPoller({
        fetchActivity: async (id, signal) => (await analysisJobService.getActivity(id, signal)).data,
        onData: (activity) => { this.activity = activity; },
        onLoading: (loading) => { this.loading = loading; },
        onError: (failed) => { this.loadError = failed; },
        onTerminal: () => { this.$emit('status-changed'); },
        onSettling: (settling) => { this.settling = settling; },
      });
      this.startSession();
    },
    beforeDestroy() { this.activityPoller.close(); },
    deactivated() { this.activityPoller.close(); },
    methods: {
      startSession() {
        this.activityPoller.close();
        this.activity = null;
        this.loading = false;
        this.loadError = false;
        this.settling = false;
        if (this.activeJobId) {
          this.activityPoller.open(this.activeJobId, this.job.status);
          this.$nextTick(this.labelDialog);
        }
      },
      labelDialog() {
        // Vuetify 2 places component attributes on its outer wrapper, away from role="dialog".
        const dialog = this.$refs.panel && this.$refs.panel.closest('[role="dialog"]');
        if (dialog) dialog.setAttribute('aria-labelledby', 'job-activity-title');
      },
      onInput(open) {
        if (!open) this.activityPoller.close();
        this.$emit('input', open);
      },
      retry() { this.activityPoller.refresh(); },
      formatTime(iso) { return iso && Number.isFinite(Date.parse(iso)) ? $formatDate(iso, 'DD MMM YYYY, HH:mm:ss') : 'Time not recorded'; },
      eventLevel(level) { return Object.prototype.hasOwnProperty.call(EVENT_ICONS, level) ? level : 'info'; },
      eventIcon(level) { return EVENT_ICONS[this.eventLevel(level)]; },
    },
  };
</script>

<style lang="scss">
  @import './aiAnalysisTokens';

  .job-activity {
    @include ai-analysis-tokens;
    display: flex;
    flex-direction: column;
    max-height: 85vh;
    overflow: hidden;
    border-radius: var(--ds-radius-md);
    background: var(--aa-surface);
    color: var(--aa-text-primary);
    font-family: var(--ds-font-family-body);
    font-size: var(--ds-font-body-sm);
    line-height: 1.5;
    p { margin: 0; overflow-wrap: anywhere; }
    h2 { margin: 0; font-family: var(--ds-font-family-heading); font-size: var(--ds-font-title); font-weight: 600; }
    h3 { margin: 0 0 var(--ds-space-md); font-size: var(--ds-font-body); font-weight: 600; }
    &__head, &__foot { display: flex; align-items: center; justify-content: space-between; gap: var(--ds-space-md); flex: 0 0 auto; padding: var(--ds-space-md) var(--ds-space-lg); }
    &__head { align-items: flex-start; border-bottom: 1px solid var(--aa-border-default); > div { min-width: 0; } }
    &__identity { color: var(--aa-text-muted); padding-top: var(--ds-space-xs); }
    &__body { overflow-y: auto; padding: var(--ds-space-lg); min-height: 100px; }
    &__summary { padding: var(--ds-space-md); margin-bottom: var(--ds-space-lg); border: 1px solid var(--aa-border-default); border-radius: var(--ds-radius-sm); background: var(--aa-surface-subtle); }
    &__summary-top { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--ds-space-sm); flex-wrap: wrap; }
    &__stage { font-size: var(--ds-font-subtitle); font-weight: 600; }
    &__muted { color: var(--aa-text-muted); }
    &__badge { flex: 0 0 auto; border: 1px solid currentColor; border-radius: var(--ds-radius-full); padding: 2px 10px; font-size: var(--ds-font-caption); font-weight: 600; color: var(--aa-status-running); }
    &__badge--completed { color: var(--aa-status-done); }
    &__badge--failed { color: var(--aa-status-failed); }
    &__badge--cancelled { color: var(--aa-text-muted); }
    &__progress { margin-top: var(--ds-space-sm); }
    &__updated { padding-top: var(--ds-space-sm); }
    &__notice { margin-bottom: var(--ds-space-md); padding: var(--ds-space-md); border-left: 3px solid var(--aa-status-stale); background: var(--aa-surface-subtle); p { color: var(--aa-text-muted); margin: var(--ds-space-xs) 0 var(--ds-space-sm); } }
    &__loading { display: flex; align-items: center; justify-content: center; gap: var(--ds-space-sm); padding: var(--ds-space-md); color: var(--aa-text-muted); }
    &__timeline { list-style: none; padding: 0 0 0 8px; margin: 0; li { position: relative; padding: 0 0 var(--ds-space-lg) var(--ds-space-lg); border-left: 1px solid var(--aa-border-default); } li:last-child { border-left-color: transparent; padding-bottom: 0; } }
    &__event-icon.v-icon { position: absolute; left: -9px; top: 1px; background: var(--aa-surface); color: var(--aa-status-running); }
    &__event--error &__event-icon.v-icon { color: var(--aa-status-failed); }
    &__event--warning &__event-icon.v-icon { color: var(--aa-status-stale); }
    &__event--success &__event-icon.v-icon { color: var(--aa-status-done); }
    &__event-meta { color: var(--aa-text-muted); font-size: var(--ds-font-caption); padding-bottom: var(--ds-space-xs); }
    &__foot { border-top: 1px solid var(--aa-border-default); .v-btn { flex: 0 0 auto; } }
    .v-btn:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: 2px; }
    @media (max-width: 480px) { &__head, &__body, &__foot { padding: var(--ds-space-md); } }
  }
  .theme--dark .job-activity { @include ai-analysis-tokens-dark; }
</style>
